"use client";

import { useEffect, useRef, useState } from "react";
import AppImage from "@/components/ui/AppImage";

export default function ContactSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    service: "",
    area: "",
    message: ""
  });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const elements = sectionRef.current?.querySelectorAll(".contact-reveal");
    if (!elements) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.remove("hidden-reveal");
        });
      },
      { threshold: 0.1 }
    );
    elements.forEach((el) => {
      el.classList.add("hidden-reveal");
      observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Connect to backend/email service
    setSubmitted(true);
  };

  return (
    <section
      id="contact"
      ref={sectionRef}
      className="py-24 md:py-32 relative overflow-hidden">
      
      {/* Background */}
      <div className="absolute inset-0 z-0">
        <AppImage
          src="https://images.unsplash.com/photo-1693113850605-7a3331499392"
          alt="Mong Kok Kowloon Hong Kong cityscape at night with neon lights"
          fill
          className="object-cover object-center" />
        
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(135deg, rgba(10,22,40,0.97) 0%, rgba(26,47,94,0.93) 100%)" }} />
        
      </div>

      <div className="relative z-10 px-6 md:px-12 max-w-screen-xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16 contact-reveal reveal">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-8 h-[1px]" style={{ background: "#D4AF37" }} />
            <span className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: "#D4AF37" }}>
              Contact Us · 聯絡我們
            </span>
            <div className="w-8 h-[1px]" style={{ background: "#D4AF37" }} />
          </div>
          <h2 className="font-display section-title text-white">
            開始您的<br />
            <span className="italic" style={{ color: "#D4AF37" }}>Dream</span> Renovation
          </h2>
          <p className="mt-4 text-base max-w-xl mx-auto" style={{ color: "rgba(245,240,232,0.6)" }}>
            免費上門諮詢及報價 · Free site visit and consultation
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left: Contact Info */}
          <div className="contact-reveal reveal space-y-8">
            {/* WhatsApp Primary CTA */}
            <a
              href="https://wa.me/85256614547"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-5 p-6 rounded-2xl transition-all duration-300 hover:scale-[1.02] group"
              style={{
                background: "linear-gradient(135deg, rgba(37,211,102,0.15), rgba(18,140,126,0.1))",
                border: "1px solid rgba(37,211,102,0.3)"
              }}>
              
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: "linear-gradient(135deg, #25D366, #128C7E)" }}>
                
                <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
              </div>
              <div>
                <div className="font-bold text-white text-lg">WhatsApp 即時查詢</div>
                <div className="text-sm" style={{ color: "rgba(245,240,232,0.7)" }}>+852 5661 4547</div>
                <div className="text-xs mt-1" style={{ color: "rgba(37,211,102,0.8)" }}>
                  Chat Now · 點擊即時對話
                </div>
              </div>
              <svg
                className="ml-auto transition-transform duration-300 group-hover:translate-x-1"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="rgba(245,240,232,0.4)"
                strokeWidth="2">
                
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </a>

            {/* Address */}
            <div
              className="p-6 rounded-2xl"
              style={{ background: "rgba(26,47,94,0.4)", border: "1px solid rgba(212,175,55,0.15)" }}>
              
              <div className="flex items-start gap-4">
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: "rgba(212,175,55,0.1)", border: "1px solid rgba(212,175,55,0.2)" }}>
                  
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="1.5">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </div>
                <div>
                  <div className="font-bold text-white mb-1">辦公室地址</div>
                  <div className="text-sm font-bold" style={{ color: "#D4AF37" }}>旺角上海街426號</div>
                  <div className="text-xs mt-0.5" style={{ color: "rgba(245,240,232,0.6)" }}>
                    426 Shanghai Street, Mong Kok, Kowloon, Hong Kong
                  </div>
                  <div className="text-xs mt-2" style={{ color: "rgba(245,240,232,0.45)" }}>
                    近旺角MTR站 · Near Mong Kok MTR Station
                  </div>
                </div>
              </div>
            </div>

            {/* Business Hours */}
            <div
              className="p-6 rounded-2xl"
              style={{ background: "rgba(26,47,94,0.4)", border: "1px solid rgba(212,175,55,0.15)" }}>
              
              <div className="font-bold text-white mb-4">辦公時間 · Business Hours</div>
              <div className="space-y-2">
                {[
                { dayZh: "星期一至五", dayEn: "Mon – Fri", hours: "9:00 – 18:00" },
                { dayZh: "星期六", dayEn: "Saturday", hours: "10:00 – 16:00" },
                { dayZh: "星期日及公眾假期", dayEn: "Sun & Public Holidays", hours: "WhatsApp Only" }].
                map((item) =>
                <div key={item.dayEn} className="flex justify-between items-center text-sm">
                    <div>
                      <span className="text-white font-medium">{item.dayZh}</span>
                      <span className="text-xs ml-2" style={{ color: "rgba(245,240,232,0.45)" }}>{item.dayEn}</span>
                    </div>
                    <span style={{ color: "#D4AF37" }}>{item.hours}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right: Inquiry Form */}
          <div className="contact-reveal reveal">
            <div
              className="p-8 rounded-2xl"
              style={{ background: "rgba(26,47,94,0.5)", border: "1px solid rgba(212,175,55,0.2)" }}>
              
              <h3 className="font-display text-2xl font-bold text-white mb-6">
                免費查詢 · Free Inquiry
              </h3>

              {submitted ?
              <div className="text-center py-12">
                  <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: "rgba(212,175,55,0.15)" }}>
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <h4 className="font-display text-xl text-white mb-2">查詢已提交！</h4>
                  <p style={{ color: "rgba(245,240,232,0.6)" }}>我們將在24小時內聯絡您。<br />We'll contact you within 24 hours.</p>
                </div> :

              <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: "#D4AF37" }}>
                        姓名 Name *
                      </label>
                      <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="陳大文"
                      className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-opacity-40 outline-none transition-all duration-300 focus:border-gold-DEFAULT"
                      style={{
                        background: "rgba(10,22,40,0.6)",
                        border: "1px solid rgba(212,175,55,0.2)",
                        color: "#F5F0E8"
                      }}
                      onFocus={(e) => {e.target.style.borderColor = "rgba(212,175,55,0.6)";}}
                      onBlur={(e) => {e.target.style.borderColor = "rgba(212,175,55,0.2)";}} />
                    
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: "#D4AF37" }}>
                        電話 Phone *
                      </label>
                      <input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+852 XXXX XXXX"
                      className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-300"
                      style={{
                        background: "rgba(10,22,40,0.6)",
                        border: "1px solid rgba(212,175,55,0.2)",
                        color: "#F5F0E8"
                      }}
                      onFocus={(e) => {e.target.style.borderColor = "rgba(212,175,55,0.6)";}}
                      onBlur={(e) => {e.target.style.borderColor = "rgba(212,175,55,0.2)";}} />
                    
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: "#D4AF37" }}>
                      服務類型 Service Type
                    </label>
                    <select
                    value={formData.service}
                    onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-300"
                    style={{
                      background: "rgba(10,22,40,0.6)",
                      border: "1px solid rgba(212,175,55,0.2)",
                      color: formData.service ? "#F5F0E8" : "rgba(245,240,232,0.4)"
                    }}>
                    
                      <option value="" style={{ background: "#0A1628" }}>選擇服務 Select Service</option>
                      <option value="full-home" style={{ background: "#0A1628" }}>全屋裝修 Full Home Renovation</option>
                      <option value="kitchen" style={{ background: "#0A1628" }}>廚房裝修 Kitchen Renovation</option>
                      <option value="bathroom" style={{ background: "#0A1628" }}>浴室翻新 Bathroom Renovation</option>
                      <option value="custom-furniture" style={{ background: "#0A1628" }}>訂造傢俬 Custom Furniture</option>
                      <option value="office" style={{ background: "#0A1628" }}>辦公室裝修 Office Renovation</option>
                      <option value="design-only" style={{ background: "#0A1628" }}>室內設計 Interior Design Only</option>
                      <option value="other" style={{ background: "#0A1628" }}>其他 Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: "#D4AF37" }}>
                      面積 Area (sq ft)
                    </label>
                    <input
                    type="text"
                    value={formData.area}
                    onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                    placeholder="例如: 600 sq ft"
                    className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-300"
                    style={{
                      background: "rgba(10,22,40,0.6)",
                      border: "1px solid rgba(212,175,55,0.2)",
                      color: "#F5F0E8"
                    }}
                    onFocus={(e) => {e.target.style.borderColor = "rgba(212,175,55,0.6)";}}
                    onBlur={(e) => {e.target.style.borderColor = "rgba(212,175,55,0.2)";}} />
                  
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: "#D4AF37" }}>
                      備註 Message
                    </label>
                    <textarea
                    rows={4}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="請描述您的裝修需求及預算... Please describe your renovation needs and budget..."
                    className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-300 resize-none"
                    style={{
                      background: "rgba(10,22,40,0.6)",
                      border: "1px solid rgba(212,175,55,0.2)",
                      color: "#F5F0E8"
                    }}
                    onFocus={(e) => {e.target.style.borderColor = "rgba(212,175,55,0.6)";}}
                    onBlur={(e) => {e.target.style.borderColor = "rgba(212,175,55,0.2)";}} />
                  
                  </div>

                  <button
                  type="submit"
                  className="w-full py-4 rounded-xl font-bold text-sm uppercase tracking-wider transition-all duration-300 hover:scale-[1.02] hover:shadow-gold"
                  style={{
                    background: "linear-gradient(135deg, #D4AF37, #A8891E)",
                    color: "#0A1628"
                  }}>
                  
                    提交查詢 · Submit Inquiry
                  </button>

                  <div className="text-center">
                    <span className="text-xs" style={{ color: "rgba(245,240,232,0.4)" }}>或 / or</span>
                  </div>

                  <a
                  href="https://wa.me/85256614547"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-3 w-full py-4 rounded-xl font-bold text-sm uppercase tracking-wider transition-all duration-300 hover:scale-[1.02]"
                  style={{
                    background: "linear-gradient(135deg, rgba(37,211,102,0.2), rgba(18,140,126,0.15))",
                    border: "1px solid rgba(37,211,102,0.3)",
                    color: "#25D366"
                  }}>
                  
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                    WhatsApp 即時查詢 Chat Now
                  </a>
                </form>
              }
            </div>
          </div>
        </div>
      </div>
    </section>);

}